const previewCache = new Map();
const pendingPreviews = new Map();
let cacheBytes = 0;
let generation = 0;

export const MATPLOTLIB_PREVIEW_CACHE_LIMIT = 70;
// Conservative UTF-16 string estimate; decoded bitmap memory is owned by the
// browser and is separate from this cache's retained source/image strings.
export const MATPLOTLIB_PREVIEW_CACHE_BYTES = 16 * 1024 * 1024;
const entryBytes = (code, image) => (code.length + String(image).length) * 2;

const removeEntry = (code) => {
  if (!previewCache.has(code)) return;
  cacheBytes -= entryBytes(code, previewCache.get(code));
  previewCache.delete(code);
};

const normalizeSourceCode = (code) => String(code || "").trim();

export const getCachedMatplotlibPreview = (code) => {
  const normalizedCode = normalizeSourceCode(code);
  if (!normalizedCode) return "";

  const image = previewCache.get(normalizedCode) || "";
  if (image) {
    previewCache.delete(normalizedCode);
    previewCache.set(normalizedCode, image);
  }
  return image;
};

export const rememberMatplotlibPreview = (code, image) => {
  const normalizedCode = normalizeSourceCode(code);
  if (!normalizedCode || !image) return;

  removeEntry(normalizedCode);
  const size = entryBytes(normalizedCode, image);
  if (size > MATPLOTLIB_PREVIEW_CACHE_BYTES) return;
  previewCache.set(normalizedCode, image);
  cacheBytes += size;
  while (previewCache.size > MATPLOTLIB_PREVIEW_CACHE_LIMIT || cacheBytes > MATPLOTLIB_PREVIEW_CACHE_BYTES) {
    removeEntry(previewCache.keys().next().value);
  }
};

const abortedResult = () => ({ success: false, aborted: true, error: "預覽已取消" });

// Callers sharing one in-flight render each hold a subscription. The request is
// aborted (and dropped from the queue) only when every subscriber has given up;
// a caller without a signal keeps it alive, as before.
export const getOrRenderMatplotlibPreview = async (code, renderPreview, { signal } = {}) => {
  const normalizedCode = normalizeSourceCode(code);
  if (!normalizedCode) return { success: false, error: "缺少 Python 程式碼。" };

  const cachedImage = getCachedMatplotlibPreview(normalizedCode);
  if (cachedImage) {
    return { success: true, image: cachedImage, cached: true };
  }
  if (signal?.aborted) return abortedResult();

  let entry = pendingPreviews.get(normalizedCode);
  if (!entry) {
    const requestGeneration = generation;
    const controller = typeof AbortController === "function" ? new AbortController() : null;
    entry = { controller, subscribers: 0 };
    entry.promise = Promise.resolve()
      .then(() => renderPreview(normalizedCode, { signal: controller?.signal }))
      .then((result) => {
        if (requestGeneration === generation && result?.success && result.image) {
          rememberMatplotlibPreview(normalizedCode, result.image);
        }
        return result;
      })
      .finally(() => {
        if (pendingPreviews.get(normalizedCode) === entry) pendingPreviews.delete(normalizedCode);
      });
    pendingPreviews.set(normalizedCode, entry);
  }

  const shared = entry;
  shared.subscribers += 1;
  if (!signal) return shared.promise;

  return new Promise((resolve, reject) => {
    const onAbort = () => {
      shared.subscribers -= 1;
      if (shared.subscribers <= 0 && pendingPreviews.get(normalizedCode) === shared) {
        pendingPreviews.delete(normalizedCode);
        shared.controller?.abort();
      }
      resolve(abortedResult());
    };
    signal.addEventListener("abort", onAbort, { once: true });
    shared.promise.then((result) => {
      signal.removeEventListener("abort", onAbort);
      resolve(result);
    }, (error) => {
      signal.removeEventListener("abort", onAbort);
      reject(error);
    });
  });
};

export const clearMatplotlibPreviewCache = () => {
  previewCache.clear();
  pendingPreviews.clear();
  cacheBytes = 0;
  generation += 1;
};
